import { supabase } from "./supabase";

// ─────────────────────────────────────────────────────────────────────────────
// Round 10 (r10e) — Forever sets on server truth. Definitions, XP and set
// composition come ONLY from get_my_forever (R25); submit_forever_set (R23) is
// the only place a submission is decided. This module is a thin, mapped wrapper
// over both RPCs — no reward logic, no client XP table.
// ─────────────────────────────────────────────────────────────────────────────

function mapSubmission(row) {
  return {
    id: row.id,
    setId: row.set_id,
    label: row.label,
    xp: row.xp,
    status: row.status,
    createdAt: row.created_at,
    selection: row.selection,
    cards: row.cards,
  };
}

/**
 * Fetch the signed-in member's Forever definitions + own submissions for a
 * season. Returns `{ season, definitions, submissions }` — `definitions` is
 * the server shape verbatim (id,label,xp,description,rarity,teams_required,
 * per_team — the input `foreverProgress` from @nrl/shared expects); `submissions`
 * is camelCased, newest first. Returns null on any error (console.warn, never
 * throws) so a failed refresh preserves the caller's last known Forever state.
 *
 * @param {number} [seasonYear]
 */
export async function getMyForever(seasonYear) {
  try {
    const { data, error } = await supabase.rpc("get_my_forever", { p_season: seasonYear ?? null });
    if (error) throw error;
    const payload = data && typeof data === "object" ? data : {};
    return {
      season: payload.season ?? seasonYear ?? null,
      definitions: Array.isArray(payload.definitions) ? payload.definitions : [],
      submissions: Array.isArray(payload.submissions) ? payload.submissions.map(mapSubmission) : [],
    };
  } catch (error) {
    console.warn("Unable to load Forever data", error);
    return null;
  }
}

const knownTokens = new Set(["forbidden"]);

/**
 * A fresh request id for one logical confirmed Forever selection. Round 10
 * remediation (M1): the CALLER generates this once per selection and holds
 * it across any uncertain-result retry of the same selection, so the
 * server's (user, request_id) replay key can actually catch a retried
 * request — a retry that mints its own id would look like a brand-new
 * submission and could double-lock/double-pay. A fresh id is minted only for
 * a deliberate new submission (a new selection).
 */
export function newForeverRequestId() {
  return crypto.randomUUID();
}

/**
 * Submit a completed Forever set. `requestId` should be the SAME id across
 * a retry of the same logical selection (see `newForeverRequestId`); when
 * omitted, one is minted here as a safety net.
 *
 * @param {string} setId
 * @param {object} selection `{}` for common, `{legacy_num}` for fullnum,
 *   `{teams:[teamId,...]}` for a team-based set.
 * @param {string} [requestId]
 * @returns {Promise<{ok:true, submissionId, setId, xp, status, replay} | {ok:false, code, detail?}>}
 */
export async function submitForeverSet(setId, selection, requestId) {
  try {
    const { data, error } = await supabase.rpc("submit_forever_set", {
      p_set_id: setId,
      p_selection: selection,
      p_request_id: requestId || crypto.randomUUID(),
    });
    if (error) {
      console.warn("Forever submit refused", error);
      const token = error.message?.split(":")[0].trim();
      return { ok: false, code: knownTokens.has(token) ? token : "server_error" };
    }
    if (data?.ok === false) return { ok: false, code: data.code, detail: data.detail };
    return {
      ok: true,
      submissionId: data.submission_id,
      setId: data.set_id,
      xp: data.xp,
      status: data.status,
      replay: Boolean(data.replay),
    };
  } catch (error) {
    console.warn("Unable to reach the Forever server", error);
    return { ok: false, code: "network_error" };
  }
}

/**
 * The team id behind every card definition, by legacy_num — needed because
 * `teams` itself carries no authenticated grant (the client resolves the
 * supported-team NAME through public_progress instead); card_definitions IS
 * authenticated-readable and carries team_id, so a team-based Forever
 * selection (which the server requires as real team ids) is built from this.
 * Returns null on error (console.warn) so a failed load just leaves team-based
 * sets un-completable rather than throwing.
 */
export async function fetchCardTeams() {
  try {
    const { data, error } = await supabase.from("card_definitions").select("legacy_num,team_id");
    if (error) throw error;
    return (data || []).map((row) => ({ legacyNum: row.legacy_num, teamId: row.team_id }));
  } catch (error) {
    console.warn("Unable to load the card catalog", error);
    return null;
  }
}

const errorCopy = {
  unknown_set: "That Forever set isn't available right now.",
  invalid_selection: "That selection isn't valid — refresh and try again.",
  set_incomplete: "Some of these cards are tied up in open trades or already locked.",
  forbidden: "Your session has expired. Please sign in again.",
  server_error: "Something went wrong — try again.",
  network_error: "Couldn't reach the server — check your connection.",
};
export function foreverErrorCopy(code) {
  return errorCopy[code] || "Something went wrong — try again.";
}
